import React, { useEffect, useRef, useState } from "react";
import { Section } from "./Section";

const SUCCESS_MESSAGE_DURATION_MS = 4000;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUCCESS_TEXT = "Message sent. We'll get back to you within 24 hours.";

const INITIAL_FORM = { name: "", email: "", company: "", message: "" };

export const ContactForm = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) {
      next.email = "Please enter your email.";
    } else if (!EMAIL_REGEX.test(form.email.trim())) {
      next.email = "Please enter a valid email.";
    }
    if (!form.message.trim()) next.message = "Please write a short message.";
    return next;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    setSuccess(true);
    setForm(INITIAL_FORM);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setSuccess(false);
    }, SUCCESS_MESSAGE_DURATION_MS);
  };

  return (
    <Section id="contact" className="bg-zinc-950 text-white py-24 lg:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] xl:grid-cols-[1fr_1.2fr] gap-10 lg:gap-12 xl:gap-16 2xl:gap-20 items-start">
        <div className="reveal-left">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FF4500] font-semibold">
            Contact
          </div>
          <h3
            className="mt-4 text-4xl sm:text-5xl font-black tracking-[-0.02em] text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Let’s Build Your IT Strategy Together
          </h3>
          <p className="mt-5 text-base xl:text-lg text-zinc-400 max-w-md xl:max-w-lg leading-relaxed">
            Tell us a little about your environment, your team and what’s
            keeping you up at night. We’ll come back within one business day.
          </p>

          <dl className="mt-10 space-y-5 text-sm">
            <div>
              <dt className="text-zinc-500 uppercase tracking-[0.18em] text-xs">Email</dt>
              <dd className="mt-1 text-white">lakshmi@conquestitservices.com</dd>
            </div>
            <div>
              <dt className="text-zinc-500 uppercase tracking-[0.18em] text-xs">Phone</dt>
              <dd className="mt-1 text-white">+91 90009 17715</dd>
              <dd className="mt-1 text-white">+91 88868 80018</dd>
            </div>
            <div>
              <dt className="text-zinc-500 uppercase tracking-[0.18em] text-xs">Hours</dt>
              <dd className="mt-1 text-white">Operations: 24/7/365</dd>
            </div>
          </dl>
        </div>

        <form
          onSubmit={handleSubmit}
          noValidate
          data-testid="contact-form"
          className="reveal-up bg-zinc-900/60 border border-white/10 rounded-2xl p-8 lg:p-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field
              id="contact-name"
              label="Name"
              required
              value={form.name}
              onChange={handleChange("name")}
              error={errors.name}
              autoComplete="name"
            />
            <Field
              id="contact-email"
              label="Email"
              type="email"
              required
              value={form.email}
              onChange={handleChange("email")}
              error={errors.email}
              autoComplete="email"
            />
          </div>
          <div className="mt-5">
            <Field
              id="contact-company"
              label="Company"
              value={form.company}
              onChange={handleChange("company")}
              optional
              autoComplete="organization"
            />
          </div>
          <div className="mt-5">
            <label
              htmlFor="contact-message"
              className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-400"
            >
              Message <span className="text-[#FF4500]">*</span>
            </label>
            <textarea
              id="contact-message"
              data-testid="contact-message-input"
              rows={5}
              value={form.message}
              onChange={handleChange("message")}
              className="mt-2 w-full bg-zinc-950/80 border border-white/10 focus:border-[#FF4500] outline-none rounded-lg px-4 py-3 text-sm text-white placeholder:text-zinc-600 transition-colors duration-200 resize-none"
              placeholder="Tell us about your environment..."
            />
            {errors.message && (
              <div
                data-testid="contact-message-error"
                className="mt-1.5 text-xs text-rose-400"
              >
                {errors.message}
              </div>
            )}
          </div>

          <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <button
              type="submit"
              data-testid="contact-submit"
              className="inline-flex items-center justify-center gap-2 bg-[#FF4500] hover:bg-[#ff5a1f] text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-200"
            >
              Send message
              <span aria-hidden="true">→</span>
            </button>
            {success && (
              <div
                role="status"
                data-testid="contact-success"
                className="text-sm font-semibold text-sky-500"
              >
                {SUCCESS_TEXT}
              </div>
            )}
          </div>
        </form>
      </div>
    </Section>
  );
};

const Field = ({
  id,
  label,
  type = "text",
  value,
  onChange,
  error,
  required,
  optional,
  autoComplete,
}) => (
  <div>
    <label
      htmlFor={id}
      className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-400"
    >
      {label}
      {required && <span className="text-[#FF4500]"> *</span>}
      {optional && <span className="text-zinc-600"> (optional)</span>}
    </label>
    <input
      id={id}
      data-testid={`${id}-input`}
      type={type}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      className="mt-2 w-full bg-zinc-950/80 border border-white/10 focus:border-[#FF4500] outline-none rounded-lg px-4 py-3 text-sm text-white placeholder:text-zinc-600 transition-colors duration-200"
    />
    {error && (
      <div data-testid={`${id}-error`} className="mt-1.5 text-xs text-rose-400">
        {error}
      </div>
    )}
  </div>
);

export default ContactForm;
